import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import {
  Mail,
  Lock,
  User,
  Eye,
  EyeOff,
  Zap,
  Shield,
  BarChart3,
  Users,
} from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { authService } from '@/services/auth.service';
import { cn } from '@/utils/cn';

const registerSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  referral_code: z.string().optional(),
});

type RegisterFormData = z.infer<typeof registerSchema>;

const features = [
  {
    icon: Zap,
    title: 'AI-Powered Agents',
    description: 'Deploy intelligent agents that work around the clock',
  },
  {
    icon: Shield,
    title: 'Enterprise Security',
    description: 'SOC 2 compliant with end-to-end encryption',
  },
  {
    icon: BarChart3,
    title: 'Real-time Analytics',
    description: 'Monitor performance with live dashboards',
  },
  {
    icon: Users,
    title: 'Team Collaboration',
    description: 'Build and manage your AI workforce together',
  },
];

export const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { register: registerUser } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  });

  useEffect(() => {
    // App.tsx resolves slug → code in localStorage on arrival
    const check = () => {
      const storedCode = localStorage.getItem('referral_code');
      if (storedCode) setValue('referral_code', storedCode);
    };
    check();
    // Small delay in case App.tsx slug validation is still in-flight
    const timer = setTimeout(check, 1000);
    return () => clearTimeout(timer);
  }, [setValue]);

  const handleGoogleLogin = async () => {
    try {
      const redirectUri = `${window.location.origin}/auth/google/callback`;
      const url = await authService.getGoogleAuthUrl(redirectUri);
      window.location.href = url;
    } catch {
      toast.error('Google signup is not available');
    }
  };

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    try {
      await registerUser(data.email, data.password, data.name, undefined, data.referral_code);
      localStorage.removeItem('referral_code');
      toast.success('Account created successfully!');
      navigate('/onboarding');
    } catch {
      toast.error('Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Brand Panel - hidden on mobile */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] relative overflow-hidden border-r-2 border-[#1a1a2e]" style={{ backgroundColor: '#FFFDF7', backgroundImage: 'repeating-linear-gradient(-45deg, transparent, transparent 24px, rgba(26,26,46,0.03) 24px, rgba(26,26,46,0.03) 26px)' }}>
        {/* Decorative retro shapes */}
        <div className="absolute top-12 left-10 w-20 h-20 bg-orange-200 border-2 border-[#1a1a2e] rounded-full opacity-50" />
        <div className="absolute top-1/4 right-12 w-14 h-14 bg-pink-200 border-2 border-[#1a1a2e] rounded-xl rotate-12 opacity-60" />
        <div className="absolute bottom-20 left-16 w-10 h-10 bg-cyan-200 border-2 border-[#1a1a2e] rotate-45 opacity-40" />
        <div className="absolute bottom-1/3 right-8 w-16 h-16 bg-yellow-200 border-2 border-[#1a1a2e] rounded-lg -rotate-6 opacity-50" />
        <div className="absolute top-2/3 left-1/3 w-8 h-8 bg-indigo-200 border-2 border-[#1a1a2e] rounded-full opacity-40" />

        {/* Brand content */}
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20 w-full">
          {/* Logo */}
          <div className="flex items-center gap-2 mb-10">
            <img src="/logo-icon.svg" alt="ChatCup" className="h-28 w-28" />
            <span className="text-4xl font-extrabold tracking-tight text-[#1a1a2e]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>ChatCup</span>
          </div>

          {/* Tagline in retro card */}
          <div className="bg-blue-500 border-2 border-[#1a1a2e] shadow-[5px_5px_0px_#1a1a2e] rounded-xl p-6 mb-8">
            <h2 className="text-2xl xl:text-3xl font-extrabold text-white leading-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Build your AI workforce.
              <br />
              Scale without limits.
            </h2>
            <p className="text-blue-100 text-sm mt-3 max-w-md">
              Deploy intelligent agents that automate your workflows, analyze data, and collaborate with your team in real time.
            </p>
          </div>

          {/* Feature highlights — retro cards */}
          <div className="grid grid-cols-2 gap-4">
            {features.map((feature, i) => {
              const colors = ['bg-yellow-100', 'bg-green-100', 'bg-purple-100', 'bg-orange-100'];
              const iconColors = ['text-yellow-600', 'text-green-600', 'text-purple-600', 'text-orange-600'];
              return (
                <div
                  key={feature.title}
                  className={`flex items-start gap-3 p-4 rounded-xl ${colors[i]} border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] hover:shadow-[5px_5px_0px_#1a1a2e] hover:translate-x-[-2px] hover:translate-y-[-2px] transition-all duration-200`}
                >
                  <div className={`flex items-center justify-center w-10 h-10 rounded-xl bg-white border-2 border-[#1a1a2e] shrink-0`}>
                    <feature.icon className={`w-5 h-5 ${iconColors[i]}`} />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-[#1a1a2e]">{feature.title}</h3>
                    <p className="text-xs text-gray-600 mt-0.5 leading-relaxed">{feature.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Right Form Panel */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 sm:px-12 relative overflow-hidden" style={{ backgroundColor: '#FFFDF7', backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 20px, rgba(59,130,246,0.04) 20px, rgba(59,130,246,0.04) 22px)' }}>
        {/* Decorative retro shapes */}
        <div className="absolute top-8 right-8 w-16 h-16 bg-yellow-200 border-2 border-[#1a1a2e] rounded-xl rotate-12 opacity-60" />
        <div className="absolute bottom-12 left-8 w-12 h-12 bg-blue-200 border-2 border-[#1a1a2e] rounded-full opacity-50" />
        <div className="absolute top-1/4 left-6 w-8 h-8 bg-green-200 border-2 border-[#1a1a2e] rotate-45 opacity-40" />
        <div className="absolute bottom-1/3 right-12 w-10 h-10 bg-purple-200 border-2 border-[#1a1a2e] rounded-lg -rotate-12 opacity-50" />

        <div className="w-full max-w-[420px] relative z-10">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <img src="/logo-icon.svg" alt="ChatCup" className="h-28 w-28" />
            <span className="text-4xl font-extrabold tracking-tight text-[#1a1a2e]" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>ChatCup</span>
          </div>

          {/* Retro Card wrapper */}
          <div className="bg-white border-2 border-[#1a1a2e] shadow-[5px_5px_0px_#1a1a2e] rounded-xl p-8">
          {/* Heading */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Create your account</h2>
            <p className="text-sm text-gray-500 mt-1.5">Get started in under a minute</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Full Name */}
            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-700">Full Name</label>
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-400" />
                <input
                  type="text"
                  placeholder="John Doe"
                  className={cn(
                    'w-full h-11 pl-10 pr-4 bg-white border-2 rounded-xl text-sm text-gray-900 placeholder:text-gray-400',
                    'transition-all duration-200',
                    'focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500',
                    errors.name ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500' : 'border-[#1a1a2e]'
                  )}
                  {...register('name')}
                />
              </div>
              {errors.name && (
                <p className="text-sm text-red-500">{errors.name.message}</p>
              )}
            </div>

            {/* Email */}
            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-700">Email</label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-400" />
                <input
                  type="email"
                  placeholder="you@example.com"
                  className={cn(
                    'w-full h-11 pl-10 pr-4 bg-white border-2 rounded-xl text-sm text-gray-900 placeholder:text-gray-400',
                    'transition-all duration-200',
                    'focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500',
                    errors.email ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500' : 'border-[#1a1a2e]'
                  )}
                  {...register('email')}
                />
              </div>
              {errors.email && (
                <p className="text-sm text-red-500">{errors.email.message}</p>
              )}
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-700">Password</label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-400" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a password"
                  className={cn(
                    'w-full h-11 pl-10 pr-11 bg-white border-2 rounded-xl text-sm text-gray-900 placeholder:text-gray-400',
                    'transition-all duration-200',
                    'focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500',
                    errors.password ? 'border-red-300 focus:ring-red-500/20 focus:border-red-500' : 'border-[#1a1a2e]'
                  )}
                  {...register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="w-[18px] h-[18px]" />
                  ) : (
                    <Eye className="w-[18px] h-[18px]" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="text-sm text-red-500">{errors.password.message}</p>
              )}
            </div>

            {/* Referral Code (optional) */}
            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-700">Referral Code (optional)</label>
              <input
                type="text"
                placeholder="Enter referral code"
                className={cn(
                  'w-full h-11 px-4 bg-white border-2 rounded-xl text-sm text-gray-900 placeholder:text-gray-400',
                  'transition-all duration-200',
                  'focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500',
                  'border-[#1a1a2e]'
                )}
                {...register('referral_code')}
              />
            </div>

            {/* Create Account button */}
            <button
              type="submit"
              disabled={isLoading}
              className={cn(
                'w-full h-11 flex items-center justify-center gap-2',
                'bg-blue-500 text-white font-bold rounded-xl border-2 border-[#1a1a2e]',
                'shadow-[3px_3px_0px_#1a1a2e] hover:shadow-[5px_5px_0px_#1a1a2e]',
                'hover:translate-x-[-2px] hover:translate-y-[-2px]',
                'transition-all duration-200',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t-2 border-[#1a1a2e]" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-white px-3 text-gray-400">or continue with</span>
            </div>
          </div>

          {/* Social login buttons */}
          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={handleGoogleLogin}
              className={cn(
                'flex items-center justify-center gap-2 h-12 px-4',
                'bg-white border-2 border-[#1a1a2e] rounded-xl',
                'text-sm font-bold text-gray-700',
                'shadow-[2px_2px_0px_#1a1a2e] hover:shadow-[3px_3px_0px_#1a1a2e]',
                'hover:translate-x-[-1px] hover:translate-y-[-1px]',
                'transition-all duration-200'
              )}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Sign up with Google
            </button>
          </div>

          {/* Sign in link */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              Already have an account?{' '}
              <Link
                to="/login"
                className="text-blue-600 hover:text-blue-700 font-bold transition-colors duration-200"
              >
                Sign in
              </Link>
            </p>
          </div>

          {/* Terms text */}
          <p className="text-center text-xs text-gray-400 mt-6">
            By creating an account, you agree to our{' '}
            <Link to="/terms" className="underline hover:text-gray-500 transition-colors duration-200">
              Terms of Service
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="underline hover:text-gray-500 transition-colors duration-200">
              Privacy Policy
            </Link>
          </p>
          </div>{/* End retro card wrapper */}
        </div>
      </div>

    </div>
  );
};
